import React, { useContext } from "react";
import PropTypes from "prop-types";

import { UserAvatar } from "components/Avatar/UserAvatar";
import { DateWithTooltip } from "components/DateWithTooltip";
import { Date } from "components/Date";
import { WorkStatusChip } from "components/WorkStatusChip/WorkStatusChip";
import { RTSDatePicker } from "components/DownDerateTrackerTable/RTSDatePicker";
import { Link } from "react-router-dom";
import { Chip, Link as MuiLink, TableSortLabel, Tooltip } from "@mui/material";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import { FeaturesContext } from "contexts/FeaturesContext";

import { UNASSIGNED } from "core/constants";

import severityColors from "assets/jss/default/severityColors";
import { styled } from "@mui/material/styles";
import { withTransientProps } from "core/utils/transientProps";

const GREEN = "#5E7B5E";
const RED = "#D9514B";
const ORANGE = "#EA8C1D";
const BLUE = "#3D4EAF";
const BLACK = "#000000";

export const CELL_COLOR_MAPPING = {
  Highest: RED,
  High: RED,
  Normal: ORANGE,
  Low: GREEN,
  Lowest: GREEN,
  Parked: BLUE,
  Online: GREEN,
  Offline: RED,
  Derated: ORANGE,
  Yes: GREEN,
  No: RED,
  default: BLACK
};

export const DefaultHeader = ({ sortBy, column, header }) => {
  const { hasFeature } = useContext(FeaturesContext);

  const index = sortBy?.findIndex(item => item.id === column.id);

  return (
    <HeaderContainer>
      <Tooltip title={header}>
        <Header $canSort>{header}</Header>
      </Tooltip>
      {column.canSort && (
        <SortWrapper>
          <Tooltip
            title={
              // sortBy will be undefined for tables that don't support multi column sorting
              sortBy === undefined ? (
                ""
              ) : (
                <MultiSortColumnTooltipTitle>
                  Multi-sort: Hold <kbd>Shift</kbd>
                </MultiSortColumnTooltipTitle>
              )
            }
          >
            <TableSortLabel
              data-action="sort"
              active={column.isSorted}
              direction={column?.isSortedDesc ? "desc" : "asc"}
            />
          </Tooltip>
          {sortBy?.length > 1 && index >= 0 && (
            <SortIndicator>{index + 1}</SortIndicator>
          )}
        </SortWrapper>
      )}
      {hasFeature("case_table_groupby") && column.canGroupBy && (
        <div data-action="group" style={{ cursor: "pointer" }}>
          <Tooltip
            title={
              <>
                {`${column.isGrouped ? "Ungroup" : "Group"} rows by ${header}`}
              </>
            }
          >
            {column.isGrouped ? <DeselectIcon /> : <SelectAllIcon />}
          </Tooltip>
        </div>
      )}
    </HeaderContainer>
  );
};
DefaultHeader.propTypes = {
  sortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  ).isRequired,
  column: PropTypes.shape({
    canSort: PropTypes.bool.isRequired,
    isSorted: PropTypes.bool.isRequired,
    isGrouped: PropTypes.bool.isRequired,
    canGroupBy: PropTypes.bool.isRequired,
    isSortedDesc: PropTypes.bool,
    id: PropTypes.string.isRequired
  }).isRequired,
  header: PropTypes.string.isRequired
};

export const CombinedHeader = ({ sortBy, column, header, subHeader }) => {
  const { hasFeature } = useContext(FeaturesContext);

  const index = sortBy?.findIndex(item => item.id === column.id);

  return (
    <HeaderContainer>
      <div>
        <Header $canSort>{header}</Header>
        <SubHeader>{subHeader}</SubHeader>
      </div>
      {column.canSort && (
        <SortWrapper>
          <Tooltip
            title={
              // sortBy will be undefined for tables that don't support multi column sorting
              sortBy === undefined ? (
                ""
              ) : (
                <MultiSortColumnTooltipTitle>
                  Multi-sort: Hold <kbd>Shift</kbd>
                </MultiSortColumnTooltipTitle>
              )
            }
          >
            <TableSortLabel
              data-action="sort"
              active={column.isSorted}
              direction={column?.isSortedDesc ? "desc" : "asc"}
            />
          </Tooltip>
          {sortBy?.length > 1 && index >= 0 && (
            <SortIndicator>{index + 1}</SortIndicator>
          )}
        </SortWrapper>
      )}
      {hasFeature("case_table_groupby") && column.canGroupBy && (
        <div data-action="group" style={{ cursor: "pointer" }}>
          <Tooltip
            title={
              <>
                {`${column.isGrouped ? "Ungroup" : "Group"} rows by ${header}`}
              </>
            }
          >
            {column.isGrouped ? <DeselectIcon /> : <SelectAllIcon />}
          </Tooltip>
        </div>
      )}
    </HeaderContainer>
  );
};
CombinedHeader.propTypes = {
  sortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  ).isRequired,
  column: PropTypes.shape({
    canSort: PropTypes.bool.isRequired,
    isSorted: PropTypes.bool.isRequired,
    isGrouped: PropTypes.bool.isRequired,
    canGroupBy: PropTypes.bool.isRequired,
    isSortedDesc: PropTypes.bool,
    id: PropTypes.string.isRequired
  }).isRequired,
  header: PropTypes.string.isRequired,
  subHeader: PropTypes.string.isRequired
};

export const DefaultCell = ({ value }) => <div>{value ?? "-"}</div>;
DefaultCell.propTypes = {
  value: PropTypes.string.isRequired
};

export const CombinedCell = ({ value, width = 100 }) => (
  <>
    <TruncatedBoldCell width={width}>{value[0] ?? "-"}</TruncatedBoldCell>
    <CellTruncated width={width}>{value[1] ?? "-"}</CellTruncated>
  </>
);
CombinedCell.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  width: PropTypes.number
};

export const ColorMappedCell = ({ value }) => (
  <ColoredCell cellValue={value}>{value ?? "-"}</ColoredCell>
);
ColorMappedCell.propTypes = {
  value: PropTypes.string.isRequired
};

export const CaseTableLinkCell = ({ value, caseId }) => {
  return (
    <MuiLink
      component={Link}
      onClick={event => event.stopPropagation()}
      to={`/pages/case-management/${caseId}`}
    >
      {value}
    </MuiLink>
  );
};
CaseTableLinkCell.propTypes = {
  value: PropTypes.string.isRequired,
  caseId: PropTypes.number.isRequired
};

export const BreakWordCell = ({ value, width = 100 }) => (
  <WordBreakCell width={width}>{value ?? "-"}</WordBreakCell>
);
BreakWordCell.propTypes = {
  value: PropTypes.string.isRequired,
  width: PropTypes.number
};

export const DateCell = ({ value, width = 100 }) => (
  <CellWithWidth width={width}>
    {value ? <DateWithTooltip date={value} /> : "-"}
  </CellWithWidth>
);
DateCell.propTypes = {
  value: PropTypes.string.isRequired,
  width: PropTypes.number
};

export const DateWithoutTooltipCell = ({ value, width = 100 }) => (
  <CellWithWidth width={width}>
    {value ? <Date date={value} /> : "-"}
  </CellWithWidth>
);
DateWithoutTooltipCell.propTypes = {
  value: PropTypes.string.isRequired,
  width: PropTypes.number
};

export const SeverityChipCell = ({ name, color }) => {
  return <ColoredChip $severityColor={color} label={name} />;
};
SeverityChipCell.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired
};

export const AssigneeNameCell = ({ firstName, lastName }) => {
  const fullName = `${firstName} ${lastName}`;

  return firstName || lastName ? (
    <Tooltip title={fullName}>
      <AssigneeName>{fullName}</AssigneeName>
    </Tooltip>
  ) : (
    UNASSIGNED
  );
};
AssigneeNameCell.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string
};

export const AvatarCell = ({ firstName, lastName }) => (
  <UserAvatar
    name={firstName || lastName ? `${firstName} ${lastName}` : UNASSIGNED}
  />
);
AvatarCell.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string
};

export const WorkStatusChipCell = ({ value }) => (
  <WorkStatusChip workOrder={value} />
);
WorkStatusChipCell.propTypes = {
  value: PropTypes.string.isRequired
};

export const ExpectedRTSCell = ({ cell, rowOriginal, width = 130 }) => (
  <RTSDatePicker cell={cell} rowOriginal={rowOriginal} />
);
ExpectedRTSCell.propTypes = {
  cell: PropTypes.object.isRequired,
  rowOriginal: PropTypes.object.isRequired,
  width: PropTypes.number
};

export const TruncatedCell = ({ value, width = 100 }) => (
  <Tooltip title={value ?? ""} disableHoverListener={!value}>
    <CellTruncated width={width}>{value ?? "-"}</CellTruncated>
  </Tooltip>
);
TruncatedCell.propTypes = {
  value: PropTypes.string,
  width: PropTypes.number
};

export const TruncatedCellLines = ({ value, width = 100, lines = 2 }) => (
  <Tooltip title={value ?? ""} disableHoverListener={!value}>
    <CellTruncatedLines width={width} lines={lines}>
      {value ?? "-"}
    </CellTruncatedLines>
  </Tooltip>
);
TruncatedCellLines.propTypes = {
  value: PropTypes.string,
  width: PropTypes.number,
  lines: PropTypes.number
};

export const CenterAlignedCell = ({ value }) => (
  <CenterAlignedDiv>{value}</CenterAlignedDiv>
);
CenterAlignedCell.propTypes = {
  value: PropTypes.string
};

const Header = styled("div")`
  font-weight: bold;
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const HeaderContainer = styled("div")`
  display: flex;
  width: max-content;
  user-select: none;
  width: 100%;
`;

const SubHeader = styled("div")`
  font-weight: normal;
`;

const CellTruncated = styled("div")`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-height: 20px;
  width: ${({ width }) => `${width ? `${width}px` : "unset"}`};
`;

const CellTruncatedLines = styled("div")`
  display: -webkit-box;
  -webkit-line-clamp: ${({ lines }) => `${lines}`};
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: ${({ width }) => `${width}px`};
`;

const TruncatedBoldCell = styled(CellTruncated)`
  font-weight: bold;
`;

const WordBreakCell = styled("div")`
  overflow-text: break-word;
  inlinesize: ${({ width }) => `${width}px`};
  width: ${({ width }) => `${width}px`};
`;

const CellWithWidth = styled("div")`
  width: ${({ width }) => `${width}px`};
`;

const ColoredCell = styled("div")`
  font-weight: bold;
  color: ${({ cellValue }) =>
    CELL_COLOR_MAPPING[cellValue] ?? CELL_COLOR_MAPPING.default};
`;

const ColoredChip = styled(Chip, withTransientProps)`
  background-color: ${({ $severityColor, theme }) =>
    severityColors(theme)[$severityColor]?.backgroundColor};
  color: ${({ $severityColor, theme }) =>
    severityColors(theme)[$severityColor]?.color};
`;

const CenterAlignedDiv = styled("div")`
  text-align: center;
`;

const SortWrapper = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: row;
`;

const SortIndicator = styled("div")`
  font-size: 70%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #009f33;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
`;

const MultiSortColumnTooltipTitle = styled("div")`
  font-weight: normal;
`;

const AssigneeName = styled("div")`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre-line;
  max-width: 100px;
`;
